import { Call } from '@/assets/icons/call';
import { PrimaryButton } from '@/components/buttons/primary-button';
import { SocialButton } from '@/components/buttons/social-button';
import { DownloadAppBanner } from '@/components/download-app-banner';
import { LoginHeader } from '@/components/headings/login-header';
import { PrimaryPhoneInput } from '@/components/phone-input/primary-phone-input';
import { useTranslation } from '@/hooks/use-translation';
import { OAuthWindowClosedError } from '@/lib/authentication';
import { consumeOAuthConsentReturn } from '@/lib/oauth-consent-return';
import { detectOS, isDesktop } from '@/lib/platform';
import { useHubSpot } from '@/providers/integration-caller/hubspot-provider';
import { amplitudeService } from '@/services/amplitude';
import { nativeApi } from '@/services/native-api/native-api';
import { supabase } from '@/services/supabase';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import { Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

const loginSearchSchema = z.object({
  source: fallback(z.string().optional(), undefined),
  email: fallback(
    z.preprocess(() => {
      const search = window.location.search;
      const match = search.match(/[?&]email=([^&]*)/);
      if (match?.[1]) {
        try {
          return decodeURIComponent(match[1]);
        } catch {
          return undefined;
        }
      }
      return undefined;
    }, z.string().email().optional()),
    undefined,
  ),
});

export const Route = createFileRoute('/login/')({
  component: HomeComponent,
  validateSearch: zodValidator(loginSearchSchema),
});

function HomeComponent() {
  const { source, email: prefillEmail } = Route.useSearch();
  const isInvitation = source === 'invitation';

  const [isSignInLoading, setIsSignInLoading] = useState(false);
  const [isEmailSignInLoading, setIsEmailSignInLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const [phoneNumber, setPhoneNumber] = useState<
    Parameters<typeof PrimaryPhoneInput>[0]['defaultValue'] | undefined
  >(undefined);
  const [email, setEmail] = useState<string>(prefillEmail ?? '');
  const [loginMethod, setLoginMethod] = useState<'phone' | 'email'>('email');

  const handleLoginMethodChange = (method: 'phone' | 'email') => {
    setLoginMethod(method);
    setError(''); // Clear error when switching modes
  };

  const { isReady: isHubSpotEnvironment } = useHubSpot();

  const navigate = useNavigate();
  const { t } = useTranslation('desktop_login');

  const handlePhoneNumberChange = async () => {
    setError('');

    if (!phoneNumber?.isValid()) {
      setError('invalid');
      return;
    }

    setIsSignInLoading(true);

    amplitudeService.logEvent('Authentication Method Chosen', { Result: 'phone' });
    amplitudeService.logEvent('Authentication Phone Input Filled');

    const { error } = await supabase.auth.signInWithOtp({
      phone: phoneNumber.number,
    });

    setIsSignInLoading(false);

    if (error) {
      toast.error(error.message, {
        position: 'top-center',
        style: {
          // @ts-expect-error
          right: !0,
        },
      });
    } else {
      navigate({
        to: '/login/otp-code',
        search: { phone: phoneNumber.formatInternational(), email: '' },
      });
    }
  };

  const handleEmailSignIn = async () => {
    setError('');

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('invalid');
      return;
    }

    setIsEmailSignInLoading(true);

    amplitudeService.logEvent('Authentication Method Chosen', { Result: 'email' });
    amplitudeService.logEvent('Authentication Email Input Filled');

    const { error } = await supabase.auth.signInWithOtp({
      email: email,
    });

    setIsEmailSignInLoading(false);

    if (error) {
      toast.error(error.message, {
        position: 'top-center',
        style: {
          // @ts-expect-error
          right: !0,
        },
      });
    } else {
      navigate({
        to: '/login/otp-code',
        search: { email: email, phone: '' },
      });
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'apple') => {
    amplitudeService.logEvent('Authentication Method Chosen', { Result: provider });

    const { error, data } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/login`,
        skipBrowserRedirect: nativeApi ? true : undefined,
        queryParams: {
          prompt: 'select_account',
        },
      },
    });

    if (error) {
      toast.error(error.message, {
        position: 'top-center',
        style: {
          // @ts-expect-error
          right: !0,
        },
      });
    }

    if (nativeApi && data.url) {
      const response = await nativeApi.openUrl.oauth.callback(data.url);

      if (response.data) {
        supabase.auth.setSession({
          access_token: response.data.accessToken,
          refresh_token: response.data.refreshToken,
        });

        const consentReturn = consumeOAuthConsentReturn();
        if (consentReturn) {
          navigate({ href: consentReturn, replace: true });
        } else {
          navigate({ to: '/' });
        }
      }

      if (error) {
        console.error(error);

        if (!(error instanceof OAuthWindowClosedError)) {
          toast.error(t('index.social.error'), {
            position: 'top-center',
            style: {
              // @ts-expect-error
              right: !0,
            },
          });
        }
      }
    } else {
      navigate({
        to: '/login',
      });
    }
  };

  const desktop = isDesktop();
  const os = detectOS();

  useEffect(() => {
    amplitudeService.logEvent('Authentication Method Displayed', {
      Origin: desktop && os[0] ? `${os[0].toUpperCase()}${os.slice(1)}` : 'Webapp',
    });
  }, []);

  const handleFormSubmit = () => {
    if (loginMethod === 'phone') {
      handlePhoneNumberChange();
    } else {
      handleEmailSignIn();
    }
  };

  return (
    <div className="w-full animate-in fade-in duration-1000 mx-0">
      <LoginHeader title={t('index.title')} />

      <div className="mt-6 w-full">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleFormSubmit();
          }}
        >
          <div className="flex flex-col items-center gap-y-3 pb-4">
            <div className="flex flex-col w-full max-w-xs mx-auto gap-y-2">
              <p className="text-body-14 font-abcnormal text-primary/60">
                {loginMethod === 'email' ? t('index.email.title') : t('index.phone.title')}
              </p>
              {loginMethod === 'email' ? (
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('index.email.placeholder')}
                  className="w-full h-[52px] px-4 py-4 border border-primary/10 rounded-2xl focus:outline-none bg-primary/3 text-primary placeholder:text-primary/60"
                />
              ) : (
                <PrimaryPhoneInput
                  onChange={setPhoneNumber}
                  className="w-full h-[52px]"
                  variant="login"
                  hideError={true}
                />
              )}
              {error && (
                <p className="flex text-body-14 text-red-500 text-center">
                  {loginMethod === 'email' ? t('index.email.invalid') : t('index.phone.invalid')}
                </p>
              )}
            </div>

            <PrimaryButton
              onClick={loginMethod === 'email' ? handleEmailSignIn : handlePhoneNumberChange}
              isLoading={loginMethod === 'email' ? isEmailSignInLoading : isSignInLoading}
              className="h-[52px] w-full max-w-xs mx-auto font-semibold rounded-2xl"
            >
              {loginMethod === 'email' ? t('index.email.cta') : t('index.phone.cta')}
            </PrimaryButton>
          </div>
        </form>
      </div>

      <div className="flex items-center my-4 max-w-xs mx-auto">
        <div className="flex-1 h-px bg-primary opacity-10" />
        <span className="px-4 text-body-14 text-primary opacity-75">or continue with</span>
        <div className="flex-1 h-px bg-primary opacity-10" />
      </div>

      {!isInvitation && (
        <div className="flex justify-center mb-3">
          <button
            type="button"
            onClick={() => handleLoginMethodChange(loginMethod === 'email' ? 'phone' : 'email')}
            className="flex w-full items-center gap-2 rounded-2xl px-2 py-4 justify-center h-[52px] bg-primary/3 hover:bg-primary-alpha-5 border border-primary/10"
          >
            {loginMethod === 'email' ? (
              <>
                <Call className="h-5 w-5 fill-primary" />
                <span className="font-semibold text-primary text-body-16">
                  {t('index.methodToggle.phone')}
                </span>
              </>
            ) : (
              <>
                <Mail className="h-5 w-5 stroke-primary" />
                <span className="font-semibold text-primary text-body-16">
                  {t('index.methodToggle.email')}
                </span>
              </>
            )}
          </button>
        </div>
      )}

      {isHubSpotEnvironment === false && (
        <div className="flex flex-row gap-3 justify-center flex-wrap sm:flex-nowrap w-full mx-auto m-0">
          <SocialButton variant="google" onClick={() => handleSocialLogin('google')}>
            Google
          </SocialButton>

          <SocialButton variant="apple" onClick={() => handleSocialLogin('apple')}>
            Apple
          </SocialButton>
        </div>
      )}

      <div className="absolute left-8 bottom-8 max-w-[180px]">
        <DownloadAppBanner dismissable initialDelay={3000} />
      </div>
    </div>
  );
}
